// "Places I want to go" — the user's own shortlist.
//
// Discovery without a way to keep something is a dead end: you find a spot
// worth a Saturday and then have to remember it yourself. Saved places are
// stored on-device (same as contributions); they'll sync once anonymous auth
// carries a stable id.

const KEY = 'go-saved-places-v1';

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) ?? [];
  } catch {
    return [];
  }
}

function persist(list) {
  localStorage.setItem(KEY, JSON.stringify(list));
  // Let any open view refresh without prop-drilling through the tree.
  window.dispatchEvent(new CustomEvent('go:saved-places-changed'));
}

/** Enough to re-render the card offline — we can't assume tiles are loaded. */
function toSaved(place, activityId) {
  return {
    id: place.id,
    name: place.name,
    type: place.type,
    lat: place.lat,
    lon: place.lon,
    distanceWhenSaved: place.distance ?? null,
    designation: place.details?.designation ?? null,
    details: place.details ?? null,
    savedFor: activityId ?? null,
    savedAt: new Date().toISOString(),
  };
}

export function isSaved(placeId) {
  return load().some((p) => p.id === placeId);
}

export function getSavedPlaces() {
  return load().sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}

export function savedCount() {
  return load().length;
}

/** Returns the new saved state, so callers can render an optimistic toggle. */
export function toggleSaved(place, activityId) {
  const list = load();
  const i = list.findIndex((p) => p.id === place.id);
  if (i >= 0) {
    list.splice(i, 1);
    persist(list);
    return false;
  }
  list.push(toSaved(place, activityId));
  persist(list);
  return true;
}

export function removeSaved(placeId) {
  persist(load().filter((p) => p.id !== placeId));
}

/**
 * Turn-by-turn hand-off. Universal Google Maps directions URL — opens the
 * native app on iOS/Android and the web app on desktop, so we don't have to
 * sniff the platform.
 */
export function directionsUrl(place) {
  // Coordinates, not the name: many of our places are unnamed ("Picnic area")
  // or share a name, and a lat/lon destination can't be misresolved.
  return `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lon}&travelmode=driving`;
}
